/// <amd-module name="Dashboards/Directory/components/DashboardDirectory" />
import * as Resources from "Dashboards/Directory/Resources";
import * as React from "react"; // Needed for the connect() call
import { Page } from "VSSUI/Page";
import { IFilter } from "VSSUI/Utilities/Filter";
import { Observer } from "VSSUI/Observer";
import { IVssLayoutManager, VssComponent, WrappedComponent, css } from "VSS/Platform/Layout";
import { Dashboard, Owner } from "../DashboardsDirectoryContracts";
import { ago, AgoFormat } from "VSSUI/Utilities/Date";
import { ColumnMore, ColumnSorting, ITableColumn, SimpleTableCell, sortItems, SortOrder, Table, TwoLineTableCell } from "VSSUI/Table";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssPersona } from "VSSUI/VssPersona";
import { Tooltip } from "VSSUI/TooltipEx";
import { IVssLocationService, getAssetLocation } from "VSS/Platform/Location";
import { IProjectData } from "Tfs/Platform/Page";
import { Link } from "VSSUI/Link";
import { IIconProps, Icon } from "VSSUI/Icon";
import { DirectoryFilterBar } from "./DirectoryFilterBar";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { IDashboardDirectoryService } from "./DashboardDirectoryService";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Header, TitleSize } from "VSSUI/Header";
import { DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { TeamPanelIconButton } from "TFS/TeamPanel/TeamPanelIcon/TeamPanelIcon";
import { FavoritesStarButton } from "Favorites/Star/FavoritesStarButton";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { Filter } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/Components/ZeroData";
import { connect } from "react-redux";
import { IDirectoryArtifact, IFavoriteData } from "VSS/Features/Directory/DirectoryContracts";
import { IDirectoryViewState } from "VSS/Features/ReduxDirectory/Contracts";
import { Card } from "VSSUI/Card";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Location } from "VSSUI/Utilities/Position";
import { TeachingBubble, TeachingBubbleCornerPlacement } from "VSSUI/TeachingBubble";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

// Interface for the mapped properties from Redux store
export interface IDashboardDirectoryMappedProps {
    _favorites: { [key: string]: IFavoriteData };
    _dashboardDataState: any;
    readyToMarkTti: boolean;
    newDashboardDirectoryLoading: boolean
}

// Interface for the DashboardDirectory component properties
export interface IDashboardDirectoryProps extends IDashboardDirectoryMappedProps {
    allDashboardData: Dashboard[];
    allOwner: Owner[];
    pageContext: IVssPageContext;
    project: IProjectData | undefined;
    _onDeleteDashboardClickRow: (row: Dashboard) => any;
    enableManageDashboardSecurityButton: boolean;
    enableDeleteDashboardButton: boolean;
    artifactIcon?: IIconProps;
    artifactName?: string;
    commandBarItems?: IHeaderCommandBarItem[];
    enableTeamButton: boolean;
    toggleFilter: () => any;
    isFilterVisible: boolean;
    canUseFavorites: boolean;
    onFavoriteToggled: (rowId: string, rowName: string, favId?: string) => any;
    markTti: (featureName: string, scenarioName: string) => any;
    ttiScenarioProps: { featureName: string; scenarioName: string };
}

const c_commonSettings = "Dashboard/CommonSettings";
const c_nwpTeachingBubbleSetting = "HasUserSeenLearningBubble";

export class DashboardDirectory extends VssComponent<IDashboardDirectoryProps> {
    private _dashboardsTableService: IDashboardDirectoryService;
    private _filter: Filter;
    private _sortingBehavior: ColumnSorting<Dashboard>;
    private disableDefaultSorting: boolean;
    private teachingBubbleProps: any[] = [];
    private activeBubble = new ObservableValue<number>(-1);
    private showTeachingBubble = new ObservableValue(false);
    
    constructor(props: IDashboardDirectoryProps) {
        super(props);
        this._dashboardsTableService = this.props.pageContext.getService<IDashboardDirectoryService>("IDashboardDirectoryService");

        this._dashboardsTableService.loadDashboards(this.props.allDashboardData, this.props._favorites, this.disableDefaultSorting);

        this._filter = this._dashboardsTableService.filter;
        this._sortingBehavior = new ColumnSorting<Dashboard>(
            (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
                const sort = (columnIndex: number, sortOrder: SortOrder, rows: Dashboard[]): Dashboard[] => {
                    return sortItems<Dashboard>(columnIndex, proposedSortOrder, this._sortingFunctions, this._columns, rows);
                };

                this._dashboardsTableService.sortRows(columnIndex, proposedSortOrder, sort);
            }
        );

        const pageHistoryService = this.props.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        pageHistoryService.registerFilter(this._filter, "dashboards-directory-filter", [
            {
                dataType: "string",
                filterKey: "keyword",
                historyBehavior: HistoryBehavior.replace
            },
            {
                dataType: "object",
                filterKey: "team",
                historyBehavior: HistoryBehavior.replace,
                mappingFunction: (teams: string[]): string => teams.join(),
                parsingFunction: (teams: string): string[] => teams.split(",")
            },
            {
                dataType: "object",
                filterKey: "dashboardType",
                historyBehavior: HistoryBehavior.replace,
                mappingFunction: (dashboardTypes: string[]): string => dashboardTypes.join(),
                parsingFunction: (dashboardTypes: string): string[] => dashboardTypes.split(",")
            }
        ]);

        this.disableDefaultSorting = false;
    }

    componentDidUpdate(prevProps: Readonly<IDashboardDirectoryProps>, snapshot?: {} | undefined): void {
        if (!prevProps.readyToMarkTti && this.props.readyToMarkTti) {
            this.props.markTti(this.props.ttiScenarioProps.featureName, this.props.ttiScenarioProps.scenarioName);
        }
        if (this.props != prevProps && this.props.allDashboardData) {
            let directoryData: Dashboard[];
            directoryData = this.updateCurrentDashboardData(this.props._dashboardDataState, this.props.allDashboardData);
            this._dashboardsTableService.loadDashboards(directoryData, this.props._favorites, this.disableDefaultSorting);
        }
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const agileSettings = await settingsService.getEntriesAsync(c_commonSettings, SettingsUserScope.Me);
        this.showTeachingBubble.value = !agileSettings[c_nwpTeachingBubbleSetting];
    }

    public render(): JSX.Element { 
        if (!this.props.newDashboardDirectoryLoading) {
            return (
                <div className="absolute-fill flex-column flex-center justify-center">
                  <Spinner size={SpinnerSize.large} label={Resources.LoadingMessage} />
                </div>
            );
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column">
                    <Header
                        title={this.props.artifactName}
                        titleSize={TitleSize.Large}
                        commandBarItems={[
                            {
                                id: "filter",
                                ariaLabel: Resources.FilterAriaLabel,
                                iconProps: { iconName: this.props.isFilterVisible ? "FilterSolid" : "Filter" },
                                onActivate: this._onFilterToggled,
                                subtle: true,
                                className: "dashboard-directory-filter"
                            },
                            ...(this.props.commandBarItems || [])
                        ]}
                    />
                    <div className="page-content page-content-top rhythm-vertical-16">
                        {this.props.isFilterVisible && this._renderFilterBar(this._filter)}
                            <Observer activeBubble={this.activeBubble} showTeachingBubble={this.showTeachingBubble}>
                                {(props: { activeBubble: number, showTeachingBubble: boolean; }) => {
                                    return props.showTeachingBubble && props.activeBubble !== -1 && document.getElementsByClassName("dashboard-directory-filter").length > 0 ? (
                                        <TeachingBubble
                                            {...this.teachingBubbleProps[props.activeBubble]}
                                            onDismiss={() => {
                                                this.showTeachingBubble.value = false;
                                                this.activeBubble.value = -1
                                                this.setTeachingBubbleSetting(
                                                    `${c_commonSettings}/${c_nwpTeachingBubbleSetting}`
                                                );
                                            }}
                                        />
                                    ) : null;
                                }}
                            </Observer>
                            <div ref={this.callbackRef} id="teaching-bubble"></div>
                            {this._renderDirectoryGrid()}
                    </div>
                </Page>
            </Surface>
        );
    };

    private callbackRef = (element: HTMLDivElement) => {
 
        this.teachingBubbleProps.push({
            anchorElement: document.getElementsByClassName("dashboard-directory-filter")[0] ? document.getElementsByClassName("dashboard-directory-filter")[0] as HTMLElement : element,
            anchorOrigin: { horizontal: Location.end, vertical: Location.end },
            primaryButtonProps:{
                text: Resources.LearnMoreTextLabel,
                target: "_blank",
                href:
                    "https://learn.microsoft.com/en-us/azure/devops/release-notes/2023/sprint-229-update#new-dashboard-directory-experience"
            },
            secondaryButtonProps: this.nextButton,
            title: Resources.NewDashboardListTextLabelHeader,
            text: Resources.NewDashboardListTextLabelContent
        });
            
        this.teachingBubbleProps.push({
            anchorElement: document.getElementsByClassName("dashboard-directory-filter")[0] ? document.getElementsByClassName("dashboard-directory-filter")[0] as HTMLElement : element,
            anchorOrigin: { horizontal: Location.end, vertical: Location.end },
            primaryButtonProps: this.backButton,
            secondaryButtonProps: this.nextButton,
            title: Resources.AdvancedFilteringTextLabelHeader,
            text:  Resources.AdvancedFilteringTextLabelContent
        });

        this.teachingBubbleProps.push({
            anchorElement: document.getElementsByClassName("dashboard-directory-modifiedby-header")[0] ? document.getElementsByClassName("dashboard-directory-modifiedby-header")[0] as HTMLElement : element,
            anchorOrigin: { horizontal: Location.start, vertical: Location.end },
            cornerPlacement: TeachingBubbleCornerPlacement.horizontal,
            primaryButtonProps: this.backButton,
            secondaryButtonProps:{
                text: Resources.GotIt,
                primary: true,
                onClick: () => {
                    this.showTeachingBubble.value = false;
                    this.setTeachingBubbleSetting(
                        `${c_commonSettings}/${c_nwpTeachingBubbleSetting}`
                    );
                    this.activeBubble.value = -1
                }
            },
            title: Resources.AdvancedSortingTextLabelHeader,
            text: Resources.AdvancedSortingTextLabelContent
        });
        
       
        this.activeBubble.value = 0;
    };

    private setTeachingBubbleSetting(settingsKey: string) {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries(
            {
                [settingsKey]: true
            },
            SettingsUserScope.Me
        );
    }

    private next = () => {
        if (this.activeBubble.value < this.teachingBubbleProps.length - 1) {
            this.activeBubble.value++;
        }
    };

    private previous = () => {
        if (this.activeBubble.value > 0) {
            this.activeBubble.value--;
        }
    };

    private nextButton = {
        text: Resources.NextButtonTextLabel,
        onClick: this.next,
        primary: true
    };

    private backButton = {
        text: Resources.PreviousButtonTextLabel,
        onClick: this.previous
    };

    private _onFilterToggled = (): void => {
        const { toggleFilter } = this.props;
        toggleFilter();
    };

    private _renderFilterBar = (filter: IFilter): JSX.Element => {
        return (
            <DirectoryFilterBar
                filter={filter}
                ownerFilterPlaceholderText={Resources.OwnerFilterPlaceholder}
                owners={this.props.allOwner}
                dashboardTypeFilterPlaceholderText={Resources.DashboardTypeFilterPlaceholder}
            />
        );
    };

    private _renderDirectoryGrid(): JSX.Element {
        const rows = this._dashboardsTableService.rows;

        return (
            <Observer rows={rows}>
                {(props: { rows: Dashboard[] }) => {
                    const hasDashboards = props.rows.length > 0;
                    return ( hasDashboards ? (
                        <Card className="flex-column flex-grow flex-center bolt-table-card">
                            <div className="flex-row">
                            <Table
                                    behaviors={[this._sortingBehavior]}
                                    columns={this._columns}
                                    itemProvider={rows}
                                ></Table>
                            </div>
                        </Card>
                        ) : (
                            <Card className="flex-center">
                                <ZeroData
                                    imagePath={getAssetLocation(this.context.pageContext, "ms.vss-features/directory/content/no-results-found.svg")}
                                    imageAltText="No Results Found"
                                    primaryText={Resources.NoResultsFound}
                                />
                            </Card>
                        )
                    );
                }}
            </Observer>
        );
    }

    private getUserDetails(dashboard: Dashboard): [string, string] | [] {
        if (!dashboard || !dashboard.modifiedBy || dashboard.modifiedBy.trim() === '' || dashboard.modifiedBy === null) {
            return [];
        }
        try {
            const userDetails = this.props.pageContext.getService<IVssIdentityService>("IVssIdentityService").getIdentityDetails(dashboard.modifiedBy);
            const modifiedDate = dashboard.modifiedDate || '0001-01-01T00:00:00Z';
            if (userDetails && new Date(modifiedDate) > new Date('0001-01-01T00:00:00Z')) {
                const displayName = userDetails.displayName || '';
                const imageUrl = userDetails.imageUrl || '';
                return [displayName, imageUrl];
            }
            else {
                return [];
            }
        }
        catch (e) {
            return [];
        }
    }

    private getDashboardModifiedByMap(dashboard: Dashboard): {
        id: string;
        name: string;
        imageUrl: string;
    }[] {
        const dashboardModifiedBy = this.props.allOwner
            .filter(o => o.id === dashboard.modifiedBy)
            .map(owner => {
                return {
                    id: owner.id,
                    name: owner.name,
                    imageUrl: owner.imageUrl,
                };
            });

        return dashboardModifiedBy;
    }

    private getDashboardOwnersMap(dashboard: Dashboard): {
        id: string;
        name: string;
        imageUrl: string;
        isTeam: boolean;
    }[] {
        const dashboardOwners = this.props.allOwner
            .filter(o => o.id === dashboard.ownerId)
            .map(owner => {
                const isTeam = dashboard.dashboardScope == 1;
                return {
                    id: owner.id,
                    name: owner.name,
                    imageUrl: owner.imageUrl,
                    isTeam: isTeam
                };
            });

        return dashboardOwners;
    }

    private getDashboardUrl(
        pageContext: IVssPageContext,
        projectName: string,
        teamName: string | undefined,
        dashboardId: string,
    ): string {
        const locationService: IVssLocationService = pageContext.getService("IVssLocationService");
        const routeValues: { [key: string]: string } = {
            project: projectName,
            id: dashboardId
        };

        if (teamName) {
            routeValues["team"] = teamName;
        }

        return locationService.routeUrl("ms.vss-dashboards-web.dashboards-new-experience-route", routeValues);
    }

    private _renderLastAccessedColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Dashboard>,
        tableItem: Dashboard
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex}>{tableItem.lastAccessedDate && new Date(tableItem.lastAccessedDate) > new Date('0001-01-01T00:00:00Z') &&
                <span>{(new Date(tableItem.lastAccessedDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)) ? Resources.TodayTextLabel : ago(new Date(tableItem.lastAccessedDate), AgoFormat.Compact)}</span>
            }</SimpleTableCell>
        );
    };

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Dashboard>,
        tableItem: Dashboard
    ): JSX.Element => {

        const dashboardOwners = this.getDashboardOwnersMap(tableItem);
        let url = '#';
        if (this.props.project && dashboardOwners && dashboardOwners.length > 0) {
            url = this.getDashboardUrl(this.props.pageContext, this.props.project.id, dashboardOwners[0].id, tableItem.id);
        }

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                contentClassName="rhythm-horizontal-8"
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                <div className="directory-row-name-cell">
                    <Tooltip overflowOnly={true} text={tableItem.name}>
                        <div className="directory-row-title flex-tooltip-host">
                            <Icon iconName="ViewDashboard" className="icon-margin" />
                            <span>
                                <Link className="directory-title" href={url} removeUnderline={true}>
                                    {tableItem.name}
                                </Link>
                            </span>
                        </div>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    };

    private _renderDescriptionColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Dashboard>,
        tableItem: Dashboard
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                {tableItem.description}
            </SimpleTableCell>
        );
    };

    private _renderTeamColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Dashboard>,
        tableItem: Dashboard
    ): JSX.Element => {

        const dashboardOwners = this.getDashboardOwnersMap(tableItem);

        if (dashboardOwners && dashboardOwners.length > 0 && dashboardOwners[0].isTeam != true) {
            return (
                <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                    <VssPersona className="icon-margin" displayName={dashboardOwners[0].name} imageUrl={dashboardOwners[0].imageUrl} size="extra-small-plus" />
                    <Tooltip overflowOnly={true} text={dashboardOwners[0].name}>
                        <span className="owner-name flex-tooltip-host">{dashboardOwners[0].name}</span>
                    </Tooltip>
                </SimpleTableCell>
            );
        }
        else if (this.props.enableTeamButton) {
            return (
                <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                    {this.props.project != undefined ?
                        <TeamPanelIconButton projectName={this.props.project.name} projectId={this.props.project.id} teamId={dashboardOwners[0].id} teamName={dashboardOwners[0].name} showText={true} />
                        : {}}
                </SimpleTableCell>
            );
        }
        else {
            return (
                <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                    {dashboardOwners.length > 0 ? <>{dashboardOwners[0].name}</> : {}}
                </SimpleTableCell>
            )
        }
    };

    private _renderModifiedByColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Dashboard>,
        tableItem: Dashboard
    ): JSX.Element => {

        const dashboardModifiedBy = this.getDashboardModifiedByMap(tableItem);
        let result: string | any[] = [];
        if (dashboardModifiedBy.length <= 0) {
            result = this.getUserDetails(tableItem);
        }

        let dateString = (tableItem.modifiedDate && new Date(tableItem.modifiedDate) > new Date('1900-01-01T00:00:00Z')) ? (new Date(tableItem.modifiedDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)) ? Resources.TodayTextLabel : ago(new Date(tableItem.modifiedDate), AgoFormat.Compact) : '';

        let displayName = '';
        let imageUrl = '';

        if (dashboardModifiedBy && dashboardModifiedBy.length > 0) {
            displayName = dashboardModifiedBy[0].name;
            imageUrl = dashboardModifiedBy[0].imageUrl;
        }
        else if (result && result.length > 0 && result[0] != '' && result[1] != '') {
            displayName = result[0];
            imageUrl = result[1];
        }

        if (displayName != '' && imageUrl != '') {
            return (
                <TwoLineTableCell
                    columnIndex={columnIndex}
                    key={tableItem.id + "-col-" + columnIndex}
                    tableColumn={tableColumn}
                    line1={
                       dateString ?
                        WithIcon({
                        iconProps: { iconName: "Clock" },
                        children: (
                            <span>
                                {dateString}
                            </span>
                            )
                        })  : ''
                    }
                    line2={
                        <span className="flex-row secondary-text">
                            <Tooltip overflowOnly={true} text={displayName}>
                                <span className="owner-name flex-tooltip-host">{displayName}</span>
                            </Tooltip>
                        </span>
                    }
                />
            );
        }

        else if(dateString){
            return (
                <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                     <div className={css("flex-row flex-center")}>
                        {Icon({ iconName: "Clock", className: "icon-margin" })}
                        <span>{dateString}</span>
                    </div>
                    
                </SimpleTableCell>
            );
        }

        else{
            return (
                <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}></SimpleTableCell>
            );
        }
    };

    private _renderFavoritesColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Dashboard>,
        tableItem: Dashboard
    ): JSX.Element => {
        const favMap = this.props._favorites[tableItem.id];

        let isFavorited = favMap ? true : false;
        const favoriteStar = this.props.canUseFavorites ? (
            <div className={css("directory-row-favorite", isFavorited && "favorited")}>
                 <FavoritesStarButton className="directory-row-favorite-icon" isFavorited={isFavorited} onToggleFavorite={(event) => this._onFavorite(event, tableItem)} />
             </div>
        ) : null;
        return (
            <SimpleTableCell contentClassName="justify-center" columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                {
                    favoriteStar
                }
            </SimpleTableCell>
        );
    };

    private _onFavorite = (event: React.MouseEvent<any>, row: Dashboard): void => {
        this.disableDefaultSorting = true;
        const { onFavoriteToggled } = this.props;
        const artifactId = row.id;
        const favoriteId = this.props._favorites[artifactId] ? this.props._favorites[artifactId].id : undefined;
        event.stopPropagation();
        if (onFavoriteToggled) {
            onFavoriteToggled(artifactId, row.name, favoriteId);
        }
    };

    private _getMenuItems = (row: Dashboard): IMenuProps => {
        const menuItems = [
            {
                id: "dashboard-permissions",
                text: Resources.ManageDashboardSecurityMenuItem,
                title: Resources.ManageDashboardSecurityMenuItem,
                iconProps: { iconName: "ShieldSolid" },
                onActivate: () => {
                    const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout(onDismiss => {
                        const props = {
                            dependencies: ["ms.vss-admin-web.security-view-common-content"],
                            wrappedType: "ms.vss-admin-web.security-panel-component",
                            onCancel: onDismiss,
                            securityViewTitle: row.name,
                            permissionSetId: "8ADF73B7-389A-4276-B638-FE1653F7EFC7",
                            permissionSetToken: JSON.stringify({
                                teamId: this._getOwnerIdForPermissionsOnRow(row),
                                dashboardId: row.id
                            })
                        };

                        return React.createElement(WrappedComponent, props);
                    });
                },
                disabled: !this.props.enableManageDashboardSecurityButton,
                rank: 100,
                groupKey: "general"
            } as IMenuItem,
            {
                id: "dashboard-delete",
                text: Resources.DeleteDashboardMenuItem,
                title: Resources.DeleteDashboardMenuItem,
                iconProps: { iconName: "Cancel" },
                onActivate: () => {
                    this._dashboardsTableService.deleteDashboard(row.id, this.props._onDeleteDashboardClickRow(row));
                },
                disabled: !this.props.enableDeleteDashboardButton,
                rank: 200,
                groupKey: "general"
            } as IMenuItem,
        ];
        return {
            id: "directory-menu",
            items: menuItems
        };
    };

    private _getOwnerIdForPermissionsOnRow = (row: Dashboard): string => {
        if (row.dashboardScope === DashboardScope.Project_Team && row.ownerId) {
            return row.ownerId;
        }
        return "00000000-0000-0000-0000-000000000000";
    };

    private onSizeColumn = (event: MouseEvent, index: number, width: number) => {
        if (this._columns != undefined && this._columns.length > 0 && index != undefined && width != undefined) {
            (this._columns[index].width as ObservableValue<number>).value = width;
        }
    }

    private _columns = [
        {
            id: "name",
            name: Resources.DashboardNameColumnHeader,
            width: new ObservableValue(-25),
            sortProps: { ariaLabelAscending: Resources.AscendingAriaLabel, ariaLabelDescending: Resources.DescendingAriaLabel },
            renderCell: this._renderNameColumn,
            onSize: this.onSizeColumn,
            minWidth: 50
        },
        {
            id: "ownerId",
            name: Resources.OwnerColumnHeader,
            width: new ObservableValue(-25),
            sortProps: { ariaLabelAscending: Resources.AscendingAriaLabel, ariaLabelDescending: Resources.DescendingAriaLabel },
            renderCell: this._renderTeamColumn,
            onSize: this.onSizeColumn,
            minWidth: 50
        },
        {
            id: "description",
            name: Resources.DescriptionColumnHeader,
            width: new ObservableValue(-25),
            sortProps: { ariaLabelAscending: Resources.AscendingAriaLabel, ariaLabelDescending: Resources.DescendingAriaLabel },
            renderCell: this._renderDescriptionColumn,
            onSize: this.onSizeColumn,
            minWidth: 50
        },
        {
            id: "lastAccessedDate",
            name: Resources.LastAccessedDateColumnHeader,
            width: new ObservableValue(-25),
            sortProps: { ariaLabelAscending: Resources.DateAscendingAriaLabel, ariaLabelDescending: Resources.DateDescendingAriaLabel },
            renderCell: this._renderLastAccessedColumn,
            onSize: this.onSizeColumn,
            minWidth: 50
        },
        {
            id: "modifiedBy",
            name: Resources.ModifiedByColumnHeader,
            width: new ObservableValue(-25),
            sortProps: { ariaLabelAscending: Resources.DateAscendingAriaLabel, ariaLabelDescending: Resources.DateDescendingAriaLabel },
            renderCell: this._renderModifiedByColumn,
            onSize: this.onSizeColumn,
            minWidth: 50,
            className: "dashboard-directory-modifiedby-header"
        },
        {
            ariaLabel: Resources.FavoriteCellAriaLabel,
            headerClassName: "flex-row justify-center",
            iconProps: { iconName: "FavoriteStar" },
            id: "favorites",
            sortProps: { ariaLabelAscending: Resources.SortFavoritesAscendingAriaLabel, ariaLabelDescending: Resources.SortFavoritesDescendingAriaLabel },
            width: new ObservableValue(-5),
            renderCell: this._renderFavoritesColumn,
            onSize: this.onSizeColumn,
            minWidth: 50
        },
        new ColumnMore(this._getMenuItems),
    ]

    private _sortingFunctions = [
        // Sort on Name column
        (a: Dashboard, b: Dashboard): number => {
            return a.name.localeCompare(b.name, navigator.language);
        },

        // Sort on Team Column
        (a: Dashboard, b: Dashboard): number => {
            const dashboardOwnersMap1 = this.getDashboardOwnersMap(a);
            const dashboardOwnersMap2 = this.getDashboardOwnersMap(b);
            if (dashboardOwnersMap1.length > 0 && dashboardOwnersMap2.length > 0)
                return dashboardOwnersMap1[0].name.localeCompare(dashboardOwnersMap2[0].name, navigator.language);
            else
                return 1;
        },

        // Sort on Description Column
        (a: Dashboard, b: Dashboard): number => {
            const description1 = a.description ?? '';
            const description2 = b.description ?? '';
            return description1.localeCompare(description2, navigator.language);
        },

        // Sort on last Accessed Date Column
        (a: Dashboard, b: Dashboard): number => {
            const lastAccessed1 = new Date(a.lastAccessedDate) ?? new Date(-8640000000000000);
            const lastAccessed2 = new Date(b.lastAccessedDate) ?? new Date(-8640000000000000);
            return lastAccessed1 > lastAccessed2 ? 1 : -1;
        },

        // Sort on Modified Column
        (a: Dashboard, b: Dashboard): number => {
            const lastModified1 = new Date(a.modifiedDate) ?? new Date(-8640000000000000);
            const lastModified2 = new Date(b.modifiedDate) ?? new Date(-8640000000000000);
            return lastModified1 > lastModified2 ? 1 : -1;
        },

        // Sort on Favorites Column
        (a: Dashboard, b: Dashboard): number => {
            const favoritesMap1 = this.props._favorites[a.id];
            const favoritesMap2 = this.props._favorites[b.id];
            if (favoritesMap1 && !favoritesMap2) {
                return 1;
            }
            if (favoritesMap2 && !favoritesMap1) {
                return -1;
            }
            return 0;
        }
    ];

    private updateCurrentDashboardData = (object1: any, object2: Dashboard[]): Dashboard[] => {
        const dashboardIds = Object.keys(object1); // Get the dashboard IDs from Object 1
        // Check if any dashboard gets deleted or added.
        if (dashboardIds.length != object2.length) {
            const filteredObject2 = object2.filter((dashboard) => {
                return dashboardIds.includes(dashboard.id); // Check if dashboard ID is present in Object 1
            });

            return filteredObject2;
        }
        return object2;
    };
}

function mapStateToProps<T extends IDirectoryArtifact>(
    state: IDirectoryViewState<T>
): IDashboardDirectoryMappedProps {
    return {
        _favorites: state.directoryState.favoriteState.favorites,
        _dashboardDataState: state.directoryState.directoryArtifactState.artifacts,
        readyToMarkTti: state.directoryState.directoryArtifactState.readyToMarkTti,
        newDashboardDirectoryLoading: state.directoryState.uiState.newDashboardDirectoryLoading
    };
}

function WithIcon(props: {
    className?: string;
    iconProps: IIconProps;
    children?: React.ReactNode;
}) {
    return (
        <div className={css(props.className, "flex-row flex-center")}>
            {Icon({ ...props.iconProps, className: "icon-margin" })}
            {props.children}
        </div>
    );
}

export const NewDashboardsDirectoryContentView = connect(
    mapStateToProps
)(DashboardDirectory);